/**
 * Subscription Gift Sync
 *
 * Syncs nested gift cart item quantities with their parent subscription quantities.
 * Rule: 1 parent = 1 gift. Gift qty always equals parent qty.
 * Only affects items with parent_relationship.parent_key. Safe for regular items.
 *
 * Cart page mode:
 *   theme.js does: await fetch('/cart/change.js') → window.location.reload()
 *   We wrap window.fetch so our sync runs INSIDE the awaited promise — before
 *   the theme gets the response and calls reload. No need to touch location.reload.
 *
 * Cart drawer mode:
 *   Listens for cart:change events dispatched by the theme.
 */

(function () {
  'use strict';

  const nativeFetch = window.fetch;

  let isSyncing = false;

  const isCartPage = !!(
    (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart')
    || window.location.pathname === '/cart'
  );

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function findGiftUpdates(cart) {
    if (!cart || !cart.items) return [];
    
    // Map of changes to avoid duplicate keys: { [key]: newQuantity }
    const updateMap = {};

    cart.items.forEach(function (item) {
      // 1. Existing logic based on parent_key linking
      const parentKey = item.parent_relationship && item.parent_relationship.parent_key;
      if (parentKey) {
        const parent = cart.items.find(function (p) { return p.key === parentKey; });
        if (!parent) {
          // Parent was completely removed from the cart, remove the child
          updateMap[item.key] = 0;
        } else if (item.quantity !== parent.quantity) {
          // Sync quantity
          updateMap[item.key] = parent.quantity;
        }
      }
    });

    // 2. Settings-based qty sync for items NOT linked via parent_key
    const settings = window.themeVariables && window.themeVariables.settings || {};
    const pairs = [
      { main: settings.crystalsSubscriptionMainProduct, gift: settings.crystalsSubscriptionGiftProduct },
      { main: settings.candleSubscriptionMainProduct,   gift: settings.candleSubscriptionGiftProduct },
      { main: settings.crystalsSubscriptionTestMainProduct,   gift: settings.crystalsSubscriptionTestGiftProduct || settings.crystalsSubscriptionGiftProduct },
      { main: settings.crystalsSubscriptionTest2MainProduct,  gift: settings.crystalsSubscriptionTest2GiftProduct || settings.crystalsSubscriptionGiftProduct },
      { main: settings.crystalsSubscriptionTest3MainProduct,  gift: settings.crystalsSubscriptionTest3GiftProduct || settings.crystalsSubscriptionGiftProduct },
      { main: settings.crystalsSubscriptionSkioMainProduct,   gift: settings.crystalsSubscriptionSkioGiftProduct },
      { main: settings.crystalsSubscriptionSkioTestMainProduct,   gift: settings.crystalsSubscriptionSkioTestGiftProduct || settings.crystalsSubscriptionSkioGiftProduct },
      { main: settings.crystalsSubscriptionSkioTest2MainProduct,  gift: settings.crystalsSubscriptionSkioTest2GiftProduct || settings.crystalsSubscriptionSkioGiftProduct },
      { main: settings.candleSubscriptionTestMainProduct,   gift: settings.candleSubscriptionTestGiftProduct || settings.candleSubscriptionGiftProduct }
    ].filter(function(p) { return p.main && p.gift; });

    pairs.forEach(function(pair) {
      if (!pair.main || !pair.gift) return;

      // Sum all quantities of the main product in the cart
      const mainQty = cart.items.reduce(function(sum, item) {
        return item.handle === pair.main ? sum + item.quantity : sum;
      }, 0);

      cart.items.forEach(function(item) {
        if (item.handle !== pair.gift) return;
        // Skip items already handled by parent_key logic
        const hasParentKey = !!(item.parent_relationship && item.parent_relationship.parent_key);
        if (hasParentKey) return;
        // Skip if already scheduled
        if (updateMap[item.key] !== undefined) return;

        if (mainQty === 0) {
          // Main is gone — remove orphaned gift
          updateMap[item.key] = 0;
        } else if (item.quantity !== mainQty) {
          // Sync gift qty to main qty
          updateMap[item.key] = mainQty;
        }
      });
    });

    // Generate valid updates
    const updates = [];
    for (const key in updateMap) {
      const currentItem = cart.items.find(function (i) { return i.key === key; });
      if (currentItem && currentItem.quantity !== updateMap[key]) {
        updates.push({ key: key, quantity: updateMap[key] });
      }
    }

    return updates;
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  async function fetchCurrentCart() {
    const res = await nativeFetch(cartRoot() + 'cart.js');
    return res.json();
  }

  async function changeCartItem(key, quantity, sections) {
    const body = { id: key, quantity: quantity };
    if (sections) body.sections = sections;
    const controller = new AbortController();
    const timeoutId = setTimeout(function () { controller.abort(); }, 10000);
    try {
      const res = await nativeFetch(cartRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal
      });
      return res.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async function runSync(cart, sections) {
    const updates = findGiftUpdates(cart);
    if (!updates.length) return null;

    isSyncing = true;
    try {
      let lastCart = null;
      for (let i = 0; i < updates.length; i++) {
        const isLast = i === updates.length - 1;
        lastCart = await changeCartItem(updates[i].key, updates[i].quantity, isLast ? sections : undefined);
      }
      return lastCart;
    } catch (err) {
      console.error('[subscription-gift-sync] sync error:', err);
      return null;
    } finally {
      isSyncing = false;
    }
  }

  function updateCartSectionDOM(html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newTbody = doc.querySelector('.cart-items__body');
      const curTbody = document.querySelector('.cart-items__body');
      if (newTbody && curTbody) curTbody.innerHTML = newTbody.innerHTML;
    } catch (e) {
      console.error('[subscription-gift-sync] section DOM update error:', e);
    }
  }

  // ─── Cart page: fetch interceptor ─────────────────────────────────────────

  if (isCartPage) {
    window.fetch = function () {
      const args = Array.prototype.slice.call(arguments);
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      const isCartChange = url.indexOf('/cart/change') !== -1;

      const promise = nativeFetch.apply(window, args);

      // Only intercept cart/change.js calls that the THEME makes (not our own sync calls)
      if (isCartChange && !isSyncing) {
        // Capture sections the theme requested so we forward them to our last sync call.
        // Shopify will then render those sections AFTER both sub + gift qty are updated.
        var themeSections = null;
        try {
          var reqBody = args[1] && args[1].body;
          if (reqBody) themeSections = JSON.parse(reqBody).sections || null;
        } catch (e) {}

        return promise.then(function (response) {
          if (!response.ok) return response;

          const cloned = response.clone();
          return cloned.json().then(async function (cart) {
            // Always request 'main-cart' section so we can patch the DOM ourselves
            // even when the theme doesn't use section rendering (reload path).
            const sections = themeSections || 'main-cart';
            const updatedCart = await runSync(cart, sections);
            if (updatedCart) {
              // Patch DOM immediately so there's no visible lag
              if (updatedCart.sections && updatedCart.sections['main-cart']) {
                updateCartSectionDOM(updatedCart.sections['main-cart']);
              }
              // Return synced cart (with sections HTML) to theme so its own
              // render also uses the correct post-sync state
              return new Response(JSON.stringify(updatedCart), {
                status: response.status,
                statusText: response.statusText,
                headers: { 'Content-Type': 'application/json' }
              });
            }
            return response;
          }).catch(function () {
            return response;
          });
        });
      }

      return promise;
    };

    // ── Sync on page load (fix stale state if gifts got out of sync) ─────────
    document.addEventListener('DOMContentLoaded', async function () {
      try {
        const cart = await fetchCurrentCart();
        if (!findGiftUpdates(cart).length) return;
        const updatedCart = await runSync(cart);
        if (updatedCart) window.location.reload();
      } catch (err) {
        console.error('[subscription-gift-sync] page-load sync error:', err);
      }
    });
  }

  // ─── Cart drawer: cart:change event ───────────────────────────────────────
  document.documentElement.addEventListener('cart:change', async function (event) {
    if (event.detail && event.detail.baseEvent === 'subscription-gift-sync') return;
    if (isSyncing) return;

    const cart = event.detail && event.detail.cart;
    if (!cart || !cart.items) return;

    const updates = findGiftUpdates(cart);
    if (!updates.length) return;

    isSyncing = true;
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    try {
      let lastResponse = null;
      for (let i = 0; i < updates.length; i++) {
        const isLast = i === updates.length - 1;
        const body = { id: updates[i].key, quantity: updates[i].quantity };
        if (isLast && sectionsToBundle.length > 0) body.sections = sectionsToBundle;

        const res = await nativeFetch(cartRoot() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (!res.ok) { console.error('[subscription-gift-sync] change failed:', res.status); return; }
        lastResponse = await res.json();
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'subscription-gift-sync', cart: lastResponse }
      }));
    } catch (err) {
      console.error('[subscription-gift-sync] drawer sync error:', err);
    } finally {
      isSyncing = false;
    }
  });
})();
